import endpoints from '@app/src/api/endpoints'
import { FetchKey } from '@app/src/api/fetchHooks'
import { useUpdateResource } from '@app/src/api/updateHooks'
import type { Option } from '@app/src/components/Form/Select'
import Select from '@app/src/components/Form/Select/ControlledSelect'
import { getOrganizationDisplayName } from '@app/src/components/Table/Cells/CompanyCell'
import { AvatarSize } from '@app/src/components/Ui/Avatar'
import TextField from '@app/src/components/Ui/TextField'
import { useAccount } from '@app/src/context/AccountContext'
import { useAmplitude } from '@app/src/context/AmplitudeContext'
import { useProvidersModal } from '@app/src/context/ProvidersModalContext'
import { useSnackbar } from '@app/src/context/SnackbarContext'
import useErrorNotification from '@app/src/hooks/errorNotification'
import { useOwnerRestriction } from '@app/src/hooks/useOwnerRestriction'
import { useWriteAccess } from '@app/src/hooks/useWriteAccess'
import CompanyAvatar from '@app/src/pages/ResourceCollection/Collections/CellComponents/CompanyAvatar'
import type { GenericOrganization, Provider } from '@app/src/types/organizations'
import type { User } from '@app/src/types/resourceExplorer'
import { insertIf } from '@app/src/utils/helpersTs'
import { resolveCompanyLogoImageUrl } from '@app/src/utils/resolveCompanyLogoImageUrl'
import { AmplitudeTrackingEvents, NotificationSeverity } from '@app/src/wf-constants'
import { BusinessOutlined, LanguageOutlined, NumbersOutlined, PlaceOutlined } from '@mui/icons-material'
import { Box, Divider, List, ListItem, ListItemAvatar, ListItemText, Stack, Typography } from '@mui/material'
import { useQueryClient } from '@tanstack/react-query'
import { capitalize } from 'lodash'
import React from 'react'
import { useForm } from 'react-hook-form'
import { useIntl } from 'react-intl'
import type { ActionButton } from '../../ActionButtons'
import HasWriteAccess from '../../HasWriteAccess'
import { useDrawer } from '../DrawerContext'
import type { DrawerViewProps } from '../DrawerView'
import DrawerView from '../DrawerView'

type DrawerViewAddOrganizationAsProviderProps = {
  provider: Provider
  providerType: string
  resource?: GenericOrganization
  onProviderConnected?: () => void
} & Omit<DrawerViewProps, 'title'>

const DrawerViewAddOrganizationAsProvider: React.FC<DrawerViewAddOrganizationAsProviderProps> = ({
  provider,
  providerType,
  resource,
  onProviderConnected,
  ...props
}) => {
  const { formatMessage } = useIntl()
  const { trackEvent } = useAmplitude()
  const { closeDrawer } = useDrawer()
  const { showSnackbar } = useSnackbar()
  const { showErrorNotification } = useErrorNotification()
  const { accountIsOwnerRestricted } = useOwnerRestriction()
  const { removeProviderFromExactMatches } = useProvidersModal()
  const queryClient = useQueryClient()
  const { mutateAsync, isPending: isLoading } = useUpdateResource<Partial<Provider>>()
  const { account } = useAccount()
  const hasWriteAccess = useWriteAccess()
  const { register, errors, setValue, control, handleSubmit, setError } = useForm<Partial<Provider>>({
    defaultValues: {
      // Pre-select existing owners from provider
      ownerUserIds: resource?.ownerUserIds && resource.ownerUserIds.length > 0 ? resource.ownerUserIds : [],
    },
  })

  const onSubmit = async (values: Partial<Provider>): Promise<void> => {
    const org: Partial<Provider> = {
      ...resource,
      organizationId: provider?.id,
      ownerUserIds: values?.ownerUserIds,
      customId: values?.customId,
    }
    await mutateAsync(
      {
        body: resource ? org : { ...values, organizationId: provider.id },
        url: resource ? endpoints.linkOrganizationWithProvider : endpoints.saveProvider,
      },
      {
        onSuccess: () => {
          trackEvent({
            name: AmplitudeTrackingEvents.Accessor.InviteSupplier.ConnectExistingCompany,
          })
          showSnackbar({
            message: formatMessage(
              { id: 'notifications.successfulLinkProvider' },
              { provider: formatMessage({ id: `general.${capitalize(providerType)}` }, { count: 1 }) },
            ),
            severity: NotificationSeverity.success,
            disableAutoClose: true,
          })
          queryClient.invalidateQueries({ queryKey: [FetchKey.ProviderCollection] })

          if (resource) {
            removeProviderFromExactMatches(resource.id)
          }

          // Callers that launch this from a multi-step flow (e.g. the supply-chain Add supplier
          // drawer) return the user to that launcher; the rest close the drawer as before.
          (onProviderConnected ?? closeDrawer)()
        },
        onError: error => {
          if (error.isValidationError) {
            error.setFormValidationErrors(setError)
            return
          }
          showErrorNotification({ requestError: error, disableAutoClose: true })
        },
      },
    )
  }

  return (
    <DrawerView
      title={formatMessage({ id: 'general.worldfavorNetwork' })}
      subTitle={provider.name}
      {...props}
      buttons={[
        ...insertIf<ActionButton>(hasWriteAccess, {
          label: formatMessage({ id: 'general.connect' }),
          variant: 'contained',
          type: 'submit',
          loading: isLoading,
        }),
      ]}
      onFormSubmit={handleSubmit(onSubmit)}
    >
      <Stack py={2} spacing={2} alignItems="center" textAlign="center">
        <CompanyAvatar
          size={AvatarSize.XXL}
          organizationName={getOrganizationDisplayName(provider)}
          imageUrl={resolveCompanyLogoImageUrl(provider)}
        />
        <Typography variant="subtitle1" noWrap>
          {provider.name}
        </Typography>
      </Stack>
      <List dense>
        <ListItem>
          <ListItemAvatar>
            <LanguageOutlined color="action" />
          </ListItemAvatar>
          <ListItemText
            primary={formatMessage({ id: 'schemas.organization.websiteAddress' })}
            secondary={provider.websiteAddress || formatMessage({ id: 'general.notAdded' })}
          />
        </ListItem>
        <ListItem>
          <ListItemAvatar>
            <BusinessOutlined color="action" />
          </ListItemAvatar>
          <ListItemText
            primary={formatMessage({ id: 'schemas.organization.industries' })}
            secondary={provider.industryNaceCode?.description || formatMessage({ id: 'general.notAdded' })}
          />
        </ListItem>
        <ListItem>
          <ListItemAvatar>
            <NumbersOutlined color="action" />
          </ListItemAvatar>
          <ListItemText
            primary={formatMessage({ id: 'schemas.organization.vatNumber' })}
            secondary={provider.vatNumber || formatMessage({ id: 'general.notAdded' })}
          />
        </ListItem>
        <ListItem>
          <ListItemAvatar>
            <NumbersOutlined color="action" />
          </ListItemAvatar>
          <ListItemText
            primary={formatMessage({ id: 'schemas.organization.registrationNumber' })}
            secondary={provider.registrationNumber || formatMessage({ id: 'general.notAdded' })}
          />
        </ListItem>
        <ListItem>
          <ListItemAvatar>
            <PlaceOutlined color="action" />
          </ListItemAvatar>
          <ListItemText
            primary={formatMessage({ id: 'schemas.organization.address' })}
            secondary={provider.locations?.[0]?.address || formatMessage({ id: 'general.notAdded' })}
          />
        </ListItem>
      </List>
      <HasWriteAccess>
        <Divider light />

        <Box px={2} mt={2}>
          <Typography variant="subtitle1" color="text.secondary">
            {formatMessage({ id: 'general.categorize' })}
          </Typography>
          <Box mt={2}>
            <Select<number, User>
              hoveringLabel
              multiple
              name="ownerUserIds"
              fieldLabel={formatMessage({ id: 'schemas.provider.ownerUsers' })}
              control={control}
              forceFetch
              enableAutoSelect
              navigation={{
                url: endpoints.colleagues,
                type: 'post',
                postObject: {
                  pagination: {
                    itemsPerPage: 9999,
                    pageNumber: 1,
                  },
                  include: [],
                  filter: [],
                },
              }}
              objectToOption={(owner: User): Option<number> => {
                const isDisabled =
                  accountIsOwnerRestricted && account?.user?.id !== undefined && owner.id === account.user.id
                const tooltip = isDisabled ? formatMessage({ id: 'schemas.provider.ownerRequired' }) : undefined

                return {
                  label: owner.name,
                  value: owner.id,
                  disabled: isDisabled,
                  tooltip,
                }
              }}
              error={errors?.ownerUserIds}
            />
          </Box>
          <Box mt={2}>
            <TextField
              inputRef={register}
              fullWidth
              name="customId"
              label={formatMessage({ id: 'schemas.organization.customId' })}
              error={Boolean(errors?.customId)}
              type="text"
              helperText={errors?.customId?.message}
              onClear={(): void => setValue('customId', '')}
            />
          </Box>
        </Box>
      </HasWriteAccess>
    </DrawerView>
  )
}

export default DrawerViewAddOrganizationAsProvider
